<template>
  <div>
    <navbar :links="links" />
    <div class="title mt-4 mb-2">
      {{ $t('twoFactorAuthentication') }}
    </div>
    <div v-if="$userprofile && $userprofile.twofactorauth_enabled">
      <p>
        {{ $t('twoFactorAuthenticationEnabledLong') }}
      </p>
      <h5>
        {{ $t('twoFactorAuthenticationAuthenticationMethod') }}
      </h5>
      <div
        v-for="method in $userprofile.authenticationmethods"
        :key="method.id"
      >
        <v-layout
          row
          class="method"
        >
          <v-flex
            xs4
            sm2
          >
            <img
              v-if="method.short_method == 'otp'"
              class="smallmethodicon"
              src="/static/twofactorauth/MSR_Web_Icon_QR.png"
            >
            <img
              v-if="method.short_method == 'sms'"
              class="smallmethodicon"
              src="/static/twofactorauth/MSR_Web_Icon_SMS.png"
            >
          </v-flex>
          <v-flex xs8>
            <p v-if="method.short_method == 'otp'">
              {{ $t('twoFactorAuthenticationProtectedByAuthenticatorApp') }}
            </p>
            <p v-if="method.short_method == 'sms'">
              {{ $t('twoFactorAuthenticationProtectedBySMSBeforeNumber') }}
              - {{ method.mobile_number_suffix }}.
              {{ $t('twoFactorAuthenticationProtectedBySMSAfterNumber') }}
            </p>
          </v-flex>
        </v-layout>
        <v-btn
          color="msr"
          round
          outline
          @click="disable(method)"
        >
          <v-icon medium>
            delete
          </v-icon>&nbsp;{{
            $t('twoFactorAuthenticationDisable')
          }}
        </v-btn>
      </div>
    </div>
    <div
      v-if="
        !configMethodInProgress &&
          $userprofile &&
          !$userprofile.twofactorauth_enabled
      "
    >
      <v-layout>
        <v-flex xs12>
          <p>
            {{ $t('twoFactorAuthenticationDisabledLong') }}
          </p>
        </v-flex>
      </v-layout>
      <v-layout
        class="methods"
        v-bind="layout"
      >
        <v-card
          class="methodcard clickable"
          :class="{activemethod: methodSelect == 'otp'}"
          @click="methodSelect = 'otp'"
        >
          <v-radio-group
            v-model="methodSelect"
            class="mt-0"
          >
            <v-radio
              color="msr"
              value="otp"
            />
          </v-radio-group>

          <div class="iconcontainer">
            <img
              class="methodicon"
              src="/static/twofactorauth/MSR_Web_Icon_QR.png"
            >
          </div>
          <h4>
            {{ $t('twoFactorAuthenticationAuthenticatorAppTitle') }}
          </h4>
          <p>
            {{ $t('twoFactorAuthenticationAuthenticatorAppDescription') }}
          </p>
          <ul>
            <li
              v-for="app in authenticatorApps"
              :key="app.name"
            >
              {{ app.name }}
              <a
                :href="app.ioslink"
                target="_blank"
              >iPhone</a>&nbsp;<a
                target="_blank"
                :href="app.googlelink"
              >Android</a>
            </li>
          </ul>
          <p />
        </v-card>
        <v-card
          class="methodcard clickable"
          :class="{activemethod: methodSelect == 'sms'}"
          @click="methodSelect = 'sms'"
        >
          <v-radio-group
            v-model="methodSelect"
            class="mt-0"
          >
            <v-radio
              color="msr"
              value="sms"
            />
          </v-radio-group>
          <div class="iconcontainer">
            <img
              class="methodicon"
              src="/static/twofactorauth/MSR_Web_Icon_SMS.png"
            >
          </div>
          <h4>
            {{ $t('twoFactorAuthenticationSMSTitle') }}
          </h4>
          <p>
            {{ $t('twoFactorAuthenticationSMSDescription') }}
          </p>
        </v-card>
      </v-layout>
      <v-layout row>
        <v-flex md12>
          <v-btn
            color="msr"
            round
            dark
            class="ml-0"
            :disabled="!methodSelect"
            @click="startSetup()"
          >
            {{ activateButtonCaption }}
          </v-btn>
        </v-flex>
      </v-layout>
    </div>
    <v-layout
      v-if="configMethodInProgress"
      row
    >
      <v-flex
        v-if="configMethodInProgress == 'sms'"
        xs-12
      >
        <h4 class="mb-2">
          {{ $t('twoFactorAuthenticationSMSTitle') }}
        </h4>
        <v-stepper v-model="step">
          <v-stepper-header>
            <v-stepper-step
              :complete="step > 1"
              step="1"
            >
              {{ $t('twoFactorAuthenticationEnterMobileNumber') }}
            </v-stepper-step>
            <v-divider />
            <v-stepper-step
              :complete="step > 2"
              step="2"
            >
              {{ $t('twoFactorAuthenticationVerifyCode') }}
            </v-stepper-step>
            <v-divider />
            <v-stepper-step step="3">
              {{ $t('twoFactorAuthenticationSetupFinish') }}
            </v-stepper-step>
          </v-stepper-header>
          <v-stepper-items>
            <v-stepper-content step="1">
              <v-card flat>
                <p>
                  {{
                    $t('twoFactorAuthenticationEnterMobileNumberDescription')
                  }}
                </p>
                <v-text-field
                  v-model="mobileNumber"
                  :label="$t('mobilePhone')"
                  :rules="[validateMobilePhoneNumber]"
                  type="text"
                  prepend-icon="phone"
                />
              </v-card>
              <v-btn
                round
                color="success"
                :disabled="!phoneNumberIsValid"
                @click="submitMobileNumber()"
              >
                <v-icon>check</v-icon>&nbsp;{{
                  $t('twoFactorAuthenticationConfirmMobileNumber')
                }}
              </v-btn>
            </v-stepper-content>
            <v-stepper-content step="2">
              <v-card>
                <p>
                  {{ $t('twoFactorAuthenticationSMSVerifyCode') }}
                </p>
                <CodeInput
                  v-if="step == 2"
                  ref="codeinput"
                  :field-width="codeInputFieldWidth"
                  @complete="verifyCode"
                />
                <p />
                <v-alert
                  outline
                  :value="codeError"
                  icon="warning"
                >
                  {{ $t('codeLoginFailed') }}
                </v-alert>
                <v-btn
                  round
                  outline
                  flat
                  @click="correctMobileNumber()"
                >
                  <v-icon>arrow_back</v-icon>&nbsp;{{ $t('goBack') }}
                </v-btn>
              </v-card>
            </v-stepper-content>
            <v-stepper-content step="3">
              <v-card flat>
                <p>
                  <v-icon
                    x-large
                    color="success"
                  >
                    check_circle
                  </v-icon>
                </p>
                <p>
                  {{ $t('twoFactorAuthenticationSetupCompleted') }}
                </p>
              </v-card>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-flex>
      <v-flex
        v-if="configMethodInProgress == 'otp'"
        xs-12
      >
        <h4 class="mb-2">
          {{ $t('twoFactorAuthenticationAuthenticatorAppTitle') }}
        </h4>
        <v-stepper v-model="step">
          <v-stepper-header>
            <v-stepper-step
              :complete="step > 1"
              step="1"
            >
              {{ $t('twoFactorAuthenticationAuthenticatorAppScanQRCode') }}
            </v-stepper-step>
            <v-divider />
            <v-stepper-step
              :complete="step > 2"
              step="2"
            >
              {{ $t('twoFactorAuthenticationVerifyCode') }}
            </v-stepper-step>
            <v-divider />
            <v-stepper-step step="3">
              {{ $t('twoFactorAuthenticationSetupFinish') }}
            </v-stepper-step>
          </v-stepper-header>
          <v-stepper-items>
            <v-stepper-content step="1">
              <v-card flat>
                <p>
                  {{
                    $t(
                      'twoFactorAuthenticationAuthenticatorAppScanQRCodeDescription'
                    )
                  }}
                </p>
                <ul>
                  <li
                    v-for="app in authenticatorApps"
                    :key="app.name"
                  >
                    {{ app.name }}
                    <a
                      :href="app.ioslink"
                      target="_blank"
                    >iPhone</a>&nbsp;<a
                      :href="app.googlelink"
                      target="_blank"
                    >Android</a>
                  </li>
                </ul>
                <canvas
                  ref="qrcode"
                  width="300"
                  height="300"
                />
                <p v-if="mobileView && authMethod">
                  <a :href="authMethod.otp_setup_url">
                    {{
                      $t('twoFactorAuthenticationAuthenticatorAppLocalSetup')
                    }}
                  </a>
                </p>
              </v-card>
              <v-btn
                round
                color="success"
                @click="toVerifyStep()"
              >
                <v-icon>check</v-icon>&nbsp;{{
                  $t('twoFactorAuthenticationAuthenticatorAppQRCodeScanned')
                }}
              </v-btn>
            </v-stepper-content>
            <v-stepper-content step="2">
              <v-card flat>
                <p>
                  {{ $t('twoFactorAuthenticationAuthenticatorAppVerifyCode') }}
                </p>
                <CodeInput
                  v-if="step == 2"
                  ref="codeinput"
                  :field-width="codeInputFieldWidth"
                  @complete="verifyCode"
                />
                <p />
                <v-alert
                  outline
                  :value="codeError"
                  icon="warning"
                >
                  {{ $t('codeLoginFailed') }}
                </v-alert>
                <p />
                <v-btn
                  round
                  outline
                  flat
                  @click="step = 1"
                >
                  <v-icon>arrow_back</v-icon>&nbsp;{{ $t('goBack') }}
                </v-btn>
              </v-card>
            </v-stepper-content>
            <v-stepper-content step="3">
              <v-card flat>
                <p>
                  <v-icon
                    x-large
                    color="success"
                  >
                    check_circle
                  </v-icon>
                </p>
                <p>
                  {{ $t('twoFactorAuthenticationSetupCompleted') }}
                </p>
              </v-card>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import CodeInput from 'vue-verification-code-input';
import QRCode from 'qrcode';

import {importGoogleLibPhonenumber} from '@/deferredImports';

import codeInputWorkarounds from '@/mixins/codeInputWorkarounds';

import Navbar from '@/components/Navbar';

export default {
  components: {
    Navbar,
    CodeInput,
  },
  mixins: [codeInputWorkarounds],
  data() {
    return {
      libphonenumber: null,

      links: ['dashboard', 'account', 'twoFactorAuthentication'],
      configMethodInProgress: null,
      authMethod: null,
      step: 1,
      codeError: false,
      mobileNumber: '',
      methodSelect: '',
      authenticatorApps: [
        {
          name: 'Google Authenticator',
          ioslink:
            'https://apps.apple.com/de/app/google-authenticator/id388497605',
          googlelink:
            'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2',
        },
        {
          name: 'Microsoft Authenticator',
          ioslink:
            'https://apps.apple.com/de/app/microsoft-authenticator/id983156458',
          googlelink:
            'https://play.google.com/store/apps/details?id=com.azure.authenticator',
        },
      ],
    };
  },
  computed: {
    layout() {
      if (this.$vuetify.breakpoint.smAndDown) {
        return {column: true};
      }
      return {row: true};
    },
    phoneNumberIsValid() {
      return this.validateMobilePhoneNumber(this.mobileNumber) == true;
    },
    activateButtonCaption() {
      if (this.methodSelect == 'sms') {
        return this.$t('twoFactorAuthenticationConfigureSMSNow');
      }
      if (this.methodSelect == 'otp') {
        return this.$t('twoFactorAuthenticationConfigureAuthenticatorAppNow');
      }
      return this.$t('twoFactorAuthenticationPleasePickMethod');
    },
    mobileView() {
      return this.$vuetify.breakpoint.xsOnly;
    },
  },
  created() {
    importGoogleLibPhonenumber().then((lib) => (this.libphonenumber = lib));
  },
  methods: {
    startSetup() {
      if (this.methodSelect == 'sms') {
        this.startSMSSetup();
      }
      if (this.methodSelect == 'otp') {
        this.startOTPSetup();
      }
    },
    startOTPSetup() {
      this.configMethodInProgress = 'otp';
      this.$api.startOTP2FASetup().then((response) => {
        this.authMethod = response.data;
        QRCode.toCanvas(this.$refs.qrcode, this.authMethod.otp_setup_url);

        // HACK: For safari, won't draw canvas otherwise.
        var ctx = this.$refs.qrcode.getContext('2d');
        ctx.fillRect(0, 0, 0, 0);
      });
    },
    startSMSSetup() {
      this.configMethodInProgress = 'sms';
    },
    toVerifyStep() {
      this.step = 2;

      this.$nextTick(() => {
        setTimeout(() => {
          this.setupPasteCatcher();
          this.focusCodeInput();
        }, 200);
      });
    },
    getPhoneUtil() {
      return this.libphonenumber.PhoneNumberUtil.getInstance();
    },
    submitMobileNumber() {
      const phoneUtil = this.getPhoneUtil();
      const puNumber = phoneUtil.parse(this.mobileNumber, 'DE');
      const formattedNumber = phoneUtil.format(
        puNumber,
        this.libphonenumber.PhoneNumberFormat.INTERNATIONAL
      );
      this.$api.startSMS2FASetup(formattedNumber).then((response) => {
        this.authMethod = response.data;
        this.toVerifyStep();
      });
    },
    verifyCode(code) {
      this.$api.verify2FAMethod(this.authMethod, code).then((response) => {
        if (response.data.verified) {
          this.step = 3;
          this.authMethod = response.data;
        } else {
          this.codeError = true;
          setTimeout(() => {
            this.codeError = false;
            this.clearCodeInput();
            this.focusCodeInput();
          }, 2000);
        }
      });
    },
    validateMobilePhoneNumber(number) {
      if (number.length < 3) {
        return this.$t('invalidPhone');
      }
      const phoneUtil = this.getPhoneUtil();
      const puNumber = phoneUtil.parse(number, 'DE');
      const validCountries = ['DE', 'AT'];
      let countryValidated = false;
      if (!phoneUtil.isValidNumber(puNumber)) {
        return this.$t('invalidPhone');
      }
      if (
        phoneUtil.getNumberType(puNumber) !=
        this.libphonenumber.PhoneNumberType.MOBILE
      ) {
        return this.$t('notMobileNumber');
      }
      validCountries.forEach((country) => {
        if (phoneUtil.isValidNumberForRegion(puNumber, country)) {
          countryValidated = true;
        }
      });
      if (!countryValidated) {
        return this.$t('onlyNumbersFromGermanyOrAustria');
      }
      return true;
    },
    correctMobileNumber() {
      this.mobileNumber = '';
      this.step = 1;
    },
    disable(method) {
      if (confirm(this.$t('twoFactorAuthenticationDisableConfirm'))) {
        this.$api.disable2FAMethod(method).then((response) => {
          // update user profile w/auth methods
          this.$api.updateUserAccount().then((response) => {
            let user = response.data;
            this.$store.commit('SET_CURRENT_USER', user);
          });
        });
      }
    },
  },
};
</script>

<style scoped>
.iconcontainer {
  text-align: left;
  margin-bottom: 2rem;
}

.methodicon {
  max-width: 120px;
  margin-right: 1rem;
  margin-left: 32px;
}

.smallmethodicon {
  max-width: 80px;
  margin-right: 1rem;
  margin-left: 8px;
}

.methodcard {
  padding: 1rem;
  margin-right: 1rem;
  margin-bottom: 1rem;
}

.activemethod {
  outline: solid 3px var(--msr-color-primary);
}

.methods {
  margin-top: 0.5rem;
}

.method {
  margin-top: 1rem;
}
</style>



// WEBPACK FOOTER //
// src/pages/TwoFactorAuthentication.vue